/**
 * PLOT PLANNER — collaboration sync engine (pure, no Firebase/React)
 * ─────────────────────────────────────────────────────────────────
 * The two hard problems of real-time co-editing, as pure functions:
 *
 *  1. diffContentToWrites(prev, next) — turn a local edit (prev→next CONTENT)
 *     into the MINIMAL set of scoped field-path writes, so we push only what
 *     changed (e.g. `cast.<id>.f.wound`), never the whole plan. Deletions of a
 *     whole map entry collapse to one delete at the entry path.
 *
 *  2. mergeRemoteContent(remote, inflight) — fold an incoming snapshot into the
 *     local view WITHOUT stomping fields the user is actively editing. `inflight`
 *     is the map of paths this client has changed but not yet seen echoed back;
 *     those keep the local value, everything else takes remote. This is what
 *     stops a collaborator's keystroke (or a stale snapshot) from yanking the
 *     word out from under you mid-type.
 *
 * Both operate in CONTENT space (the id-keyed-map shape from planModel), whose
 * keys are all dot-safe (ids / hardcoded field names / `shape_ai`) — EXCEPT
 * dynasty.allegiance, whose keys are house-name strings, so it is treated as an
 * opaque whole value (never descended into). Arrays are always whole values
 * (beat lists, faction members, order arrays), matching the coarse atoms in the
 * design (docs/collab-sketch.md §2.1).
 */

// Sentinel for "delete this field" in a write set — planStore maps it to
// Firestore's deleteField(). A unique object so it can never collide with data.
export const DELETE_SENTINEL = Object.freeze({ __plannerDelete: true });

// Content sub-trees written whole rather than descended into (house-name keys are
// dot-unsafe, so these maps are written as one field, never per-key).
const OPAQUE_PATHS = new Set(['dynasty.allegiance', 'dynasty.houseColors']);

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

export function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === 'object') {
    const aArr = Array.isArray(a); const bArr = Array.isArray(b);
    if (aArr !== bArr) return false;
    const ka = Object.keys(a); const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}

/* Read a dotted path from a content object; undefined if any hop is missing. */
export function getPath(obj, path) {
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (!isPlainObject(cur) && !Array.isArray(cur)) return undefined;
    cur = cur[p];
    if (cur === undefined) return undefined;
  }
  return cur;
}

/* Write a dotted path into a content object, creating intermediate objects. */
export function setPath(obj, path, value) {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!isPlainObject(cur[p])) cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return obj;
}

/* Delete a dotted path from a content object (no-op if absent). */
export function deletePath(obj, path) {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!isPlainObject(cur[p])) return obj;
    cur = cur[p];
  }
  delete cur[parts[parts.length - 1]];
  return obj;
}

function diffInto(path, prev, next, out) {
  if (deepEqual(prev, next)) return;
  const opaque = OPAQUE_PATHS.has(path);
  if (!opaque && isPlainObject(prev) && isPlainObject(next)) {
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
    keys.forEach((k) => {
      const child = path ? `${path}.${k}` : k;
      if (!(k in next)) out[child] = DELETE_SENTINEL;               // removed → delete
      else if (!(k in prev)) out[child] = clone(next[k]);           // added → set whole
      else diffInto(child, prev[k], next[k], out);                  // changed → descend
    });
  } else {
    // Leaf: array, opaque map, primitive, or a type change — write whole.
    out[path] = next === undefined ? DELETE_SENTINEL : clone(next);
  }
}

/* Minimal scoped writes to turn `prev` content into `next` content.
   Returns { "<dotted.path>": value | DELETE_SENTINEL }. */
export function diffContentToWrites(prevContent, nextContent) {
  const out = {};
  diffInto('', prevContent || {}, nextContent || {}, out);
  return out;
}

/* Fold a remote snapshot into local, preserving in-flight local edits.
   `inflight` is a Map(path → local value | DELETE_SENTINEL) of edits this client
   has made but not yet seen confirmed by the server. Those paths keep the local
   value (or stay deleted); everything else is remote. Returns a new content obj. */
export function mergeRemoteContent(remoteContent, inflight) {
  const result = clone(remoteContent) || {};
  if (inflight && inflight.size) {
    inflight.forEach((val, path) => {
      if (val === DELETE_SENTINEL) deletePath(result, path);
      else setPath(result, path, clone(val));
    });
  }
  return result;
}

/* Has the remote caught up to an in-flight local value at `path`? (Used to
   retire a path from the in-flight set once the server echoes it back.) */
export function remoteConfirms(remoteContent, path, localValue) {
  const rv = getPath(remoteContent, path);
  return localValue === DELETE_SENTINEL ? rv === undefined : deepEqual(rv, localValue);
}
